<template>
  <div :key="resourceKey">
    <loader v-if="loading" position="fixed" />

    <b-form
      v-if="prefillParams.settings"
      novalidate
      ref="form"
      @submit.prevent="debounceSubmit"
      :validated="formValidated"
    >
      <!--General Start-->
      <h3 class="text-uppercase">
        {{ $t('resources.block_titles.general') }}
      </h3>
      <!--General Info-->
      <ResourceWrapper :block="fields.general">
        <template slot="body">
          <ResourcesView
            @addItem="
              (obj) => {
                prepareParams(params, obj);
              }
            "
            :block="fields.general"
            :prefill-params="prefillParams"
          />
        </template>
      </ResourceWrapper>

      <!--Business Info-->
      <ResourceWrapper :block="fields.business">
        <template slot="body">
          <b-row>
            <b-col lg="8">
              <ResourcesView
                @addItem="
                  (obj) => {
                    prepareParams(params.settings.business, obj);
                  }
                "
                :block="fields.business"
                :prefill-params="prefillParams.settings.business"
              />
              <b-button class="px-4" :to="'/pdf'" target="_blank">
                {{ $t('pdf.edit') }}
              </b-button>
            </b-col>
            <b-col lg="4" class="mt-lg-0 mt-3">
              <div class="pr-0 pl-0" style="max-height: 400px">
                <label class="d-block">
                  {{ $t('global.logo') }}
                </label>
                <image-uploader :image="logo" />
              </div>
            </b-col>
          </b-row>
        </template>
      </ResourceWrapper>
      <!---->

      <!--Plan Settings-->
      <ResourceWrapper :block="fields.plan">
        <template slot="body">
          <ResourcesView
            @addItem="
              (obj) => {
                prepareParams(params.settings, obj);
              }
            "
            :block="fields.plan"
            :prefill-params="prefillParams.settings"
          />
        </template>
      </ResourceWrapper>
      <!---->

      <!--General END-->

      <!--Access Settings Start-->
      <h3 class="text-uppercase">
        {{ $t('resources.block_titles.access_settings') }}
      </h3>

      <!--General access-->
      <ResourceWrapper :block="fields.content_access">
        <template slot="body">
          <ResourcesView
              @addItem="
              (obj) => {
                prepareParams(params.settings, obj);
              }
            "
              :block="fields.content_access"
              :prefill-params="prefillParams.settings"
          />
        </template>
      </ResourceWrapper>
      <!---->

      <!--Predictive Paywall Settings-->
      <ResourceWrapper :block="fields.predictivePaywall">
        <template slot="body">
          <ResourcesView
              @addItem="
              (obj) => {
                prepareParams(params.settings.predictive_paywall, obj);
              }
            "
              :block="fields.predictivePaywall"
              :prefill-params="prefillParams.settings.predictive_paywall"
          />
        </template>
      </ResourceWrapper>
      <!---->

      <!--Compromised sessions-->
      <ResourceWrapper :block="fields.compromised_sessions">
        <template slot="body">
          <ResourcesView
            @addItem="
              (obj) => {
                prepareParams(params.settings, obj);
              }
            "
            :block="fields.compromised_sessions"
            :prefill-params="prefillParams.settings"
          />
        </template>
      </ResourceWrapper>
      <!---->

      <!--Black List-->
      <ResourceWrapper v-if="params.settings.blacklist" :block="fields.blacklist">
        <template slot="body">
          <ResourcesView
            @addItem="
              (obj) => {
                prepareParamsArray('blacklist', obj, index);
              }
            "
            v-for="(blacklist, index) in params.settings.blacklist"
            :key="index"
            :block="fields.blacklist"
            :prefill-params="blacklist"
          >
            <template
              slot="editButtons"
              v-if="index !== params.settings.blacklist.length && index !== 0"
            >
              <b-col class="align-self-end mb-4">
                <b-button
                  @click="deleteItem(params.settings.blacklist, index)"
                  variant="outline-primary"
                  size="sm"
                  style="height: 40px; width: 40px"
                >
                  <i class="far fa-trash-alt fa-lg" />
                </b-button>
              </b-col>
            </template>
            <template slot="editButtons" v-else>
              <b-col class="align-self-end mb-4">
                <b-button
                  @click="addItem('blacklist', { domain: '', msg: '', notify: false })"
                  variant="outline-primary"
                  size="sm"
                  style="height: 40px; width: 40px"
                >
                  <i class="fas fa-plus fa-lg" />
                </b-button>
              </b-col>
            </template>
          </ResourcesView>
        </template>
      </ResourceWrapper>
      <!---->

      <!--IP Mask Access-->
      <ResourceWrapper
          v-if="params.settings.mask_ips_users"
          :block="fields.maskIPsUsers"
          :loading="loaders['mask_ips_users']"
          @show.once="openIPAddresses('mask_ips_users', 'maskIPsUsers')"
      >
        <template slot="body">
          <b-col>
            <b-row class="align-items-center mb-2">
              <h5 class="m-0">
                {{ $t('resources.block_titles.add_ip_mask_rule') }}
              </h5>
              <b-button
                class="ml-3"
                @click="addItem('mask_ips_users', { mask_ip: '', user_id: null })"
                variant="outline-primary"
                size="sm"
                style="height: 40px; width: 40px"
              >
                <i class="fas fa-plus fa-lg" />
              </b-button>
            </b-row>
          </b-col>
          <ResourcesView
            @addItem="
              (obj) => {
                prepareParamsArray('mask_ips_users', obj, index);
              }
            "
            v-for="(mask_ips_users, index) in params.settings.mask_ips_users"
            :key="index"
            :block="fields.maskIPsUsers"
            :prefill-params="mask_ips_users"
          >
            <template slot="editButtons">
              <b-col class="align-self-end mb-4">
                <b-button
                  @click="deleteItem(params.settings.mask_ips_users, index)"
                  variant="outline-primary"
                  size="sm"
                  style="height: 40px; width: 40px"
                >
                  <i class="far fa-trash-alt fa-lg" />
                </b-button>
              </b-col>
            </template>
          </ResourcesView>
        </template>
      </ResourceWrapper>
      <!---->

      <!--IP Mask Range Access-->
      <ResourceWrapper
          v-if="params.settings.range_ips_users"
          :block="fields.ipUsersRange"
          :loading="loaders['range_ips_users']"
          @show.once="openIPAddresses('range_ips_users', 'ipUsersRange')"
      >
        <template slot="body">
          <b-col>
            <b-row class="align-items-center mb-2">
              <h5 class="m-0">
                {{ $t('resources.block_titles.add_ip_mask_rule') }}
              </h5>
              <b-button
                  class="ml-3"
                  @click="addItem('range_ips_users', { ip_from: '', ip_to: '', user_id: null })"
                  variant="outline-primary"
                  size="sm"
                  style="height: 40px; width: 40px"
              >
                <i class="fas fa-plus fa-lg" />
              </b-button>
            </b-row>
          </b-col>
          <ResourcesView
              @addItem="
              (obj) => {
                prepareParamsArray('range_ips_users', obj, index);
              }
            "
              v-for="(mask_ips_users, index) in params.settings.range_ips_users"
              :key="index"
              :block="fields.ipUsersRange"
              :prefill-params="mask_ips_users"
          >
            <template slot="editButtons">
              <b-col class="align-self-end mb-4">
                <b-button
                    @click="deleteItem(params.settings.range_ips_users, index)"
                    variant="outline-primary"
                    size="sm"
                    style="height: 40px; width: 40px"
                >
                  <i class="far fa-trash-alt fa-lg" />
                </b-button>
              </b-col>
            </template>
          </ResourcesView>
        </template>
      </ResourceWrapper>
      <!---->

      <!--Service Accounts-->
      <ResourceWrapper v-if="isRoot" :block="{ title: 'Service Accounts' }">
        <template slot="body">
          <ServiceAccountsEdit />
        </template>
      </ResourceWrapper>
      <!---->

      <!--Access Settings END-->

      <!-- Payment Services BLOCK START -->
      <h3 class="text-uppercase">
        {{ $t('resources.block_titles.payment_services') }}
      </h3>
      <!--Payment Systems Settings-->
      <ResourceWrapper :block="fields.default_payment_system">
        <template slot="body">
          <div ref="paymentSystemsSettings">
            <loader v-if="paymentSystemsLoading" />

            <ResourcesView
              @addItem="
                (obj) => {
                  prepareParams(params.settings, obj);
                }
              "
              :block="fields.default_payment_system"
              :prefill-params="prefillParams.settings"
              class="mb-3"
            />
            <!--stripe-->
            <b-row v-if="params.settings.default_paysystem == 'stripe' && params.settings.stripe">
              <b-col>
                <h5 class="mb-3">
                  <i :class="[fields.stripe.icon, 'mr-1', 'fa-lg']" />
                  {{ fields.stripe.title }}
                </h5>
                <b-row>
                  <b-col>
                    <ResourcesView
                      @addItem="
                        (obj) => {
                          prepareParams(params.settings, obj);
                        }
                      "
                      :show-title="true"
                      :block="fields.stripe.general"
                      :prefill-params="prefillParams.settings"
                    />
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>
                    <ResourcesView
                      v-if="!prefillParams.stripe_connect.test.stripe_connect_status"
                      @addItem="
                        (obj) => {
                          prepareParams(params.settings.stripe, obj, 'test');
                        }
                      "
                      :show-title="true"
                      :block="fields.stripe.test"
                      :prefill-params="params.settings.stripe.test"
                      disable-all
                    >
                      <StripeConnectBtn
                        slot="title-suffix"
                        :href="prefillParams.stripe_connect.test.stripe_connect_url"
                      />
                    </ResourcesView>
                    <ResourcesView
                      v-else
                      :show-title="true"
                      :block="fields.stripe_connect.test"
                      :prefill-params="prefillParams.stripe_connect.test"
                      disable-all
                    >
                      <StripeConnectBtn
                        slot="title-suffix"
                        :connect="false"
                        @disconnect="disconnectStripe('test')"
                      />
                    </ResourcesView>

                    <b-row>
                      <b-col v-if="checkIfEditable('stripe_connect_fee')" cols="3">
                        <ResourcesView
                          @addItem="
                            (obj) => {
                              prepareParams(params, obj, 'settings');
                            }
                          "
                          :show-title="true"
                          :block="fields.stripe_connect.fee_wallkit"
                          :prefill-params="params.settings"
                        />
                      </b-col>
                      <b-col v-if="checkIfEditable('stripe_connect_fee_ti_event')" cols="3">
                        <ResourcesView
                          @addItem="
                            (obj) => {
                              prepareParams(params, obj, 'settings');
                            }
                          "
                          :show-title="true"
                          :block="fields.stripe_connect.fee_ti_event"
                          :prefill-params="params.settings"
                        />
                      </b-col>
                    </b-row>
                  </b-col>

                  <b-col>
                    <ResourcesView
                      v-if="!prefillParams.stripe_connect.live.stripe_connect_status"
                      @addItem="
                        (obj) => {
                          prepareParams(params.settings.stripe, obj, 'live');
                        }
                      "
                      :show-title="true"
                      :block="fields.stripe.live"
                      :prefill-params="params.settings.stripe.live"
                      disable-all
                    >
                      <StripeConnectBtn
                        slot="title-suffix"
                        :href="prefillParams.stripe_connect.live.stripe_connect_url"
                      />
                    </ResourcesView>
                    <ResourcesView
                      v-else
                      :show-title="true"
                      :block="fields.stripe_connect.live"
                      :prefill-params="prefillParams.stripe_connect.live"
                      disable-all
                    >
                      <StripeConnectBtn
                        slot="title-suffix"
                        :connect="false"
                        @disconnect="disconnectStripe('live')"
                      />
                    </ResourcesView>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
            <!---->

            <!--square-->
            <b-row v-if="params.settings.default_paysystem == 'square' && params.settings.square">
              <b-col>
                <h5 class="mb-3">
                  {{ fields.square.title }}
                </h5>
                <ResourcesView
                  @addItem="
                    (obj) => {
                      prepareParams(params.settings.square, obj, 'test');
                    }
                  "
                  :show-title="true"
                  :block="fields.square.test"
                  :prefill-params="params.settings.square.test"
                />
                <ResourcesView
                  @addItem="
                    (obj) => {
                      prepareParams(params.settings.square, obj, 'live');
                    }
                  "
                  :show-title="true"
                  :block="fields.square.live"
                  :prefill-params="params.settings.square.live"
                />
              </b-col>
            </b-row>
          </div>
        </template>
      </ResourceWrapper>
      <!---->
      <!-- Payment Services BLOCK END -->

      <!-- Transactional E-MAIL Settings BLOCK -->
      <h3 class="text-uppercase">
        {{ $t('resources.block_titles.transactional_email_settings') }}
      </h3>

      <!--Emails From-->
      <ResourceWrapper v-if="params.settings.emails_from" :block="fields.emails_from">
        <template slot="body">
          <ResourcesView
            @addItem="
              (obj) => {
                prepareParams(params.settings, obj, 'emails_from');
              }
            "
            :prefill-params="params.settings.emails_from"
            :block="fields.emails_from"
            :disabled-fields="!isRoot ? ['email'] : false"
          />
        </template>
      </ResourceWrapper>
      <!---->

      <!--Emails Reply-to-->
      <ResourceWrapper v-if="params.settings.emails_replay_to" :block="fields.emails_replay_to">
        <template slot="body">
          <ResourcesView
            @addItem="
              (obj) => {
                prepareParams(params.settings, obj, 'emails_replay_to');
              }
            "
            :prefill-params="params.settings.emails_replay_to"
            :block="fields.emails_replay_to"
          />
        </template>
      </ResourceWrapper>
      <!---->

      <!--Admin Emails-->
      <ResourceWrapper :block="fields.admin_emails">
        <template slot="body">
          <div class="text-muted mb-3">
            {{ $t('resources.descriptions.email_notifications_admins') }}
          </div>

          <!--Email notifications for admins-->
          <b-row>
            <!--Admin Emails-->
            <b-col lg="6" v-if="params.settings.admin_emails">
              <div>
                <h5 class="mb-3">
                  {{ fields.admin_emails.title }}
                </h5>

                <ResourcesView
                  v-for="(item, index) in params.settings.admin_emails"
                  :key="index"
                  @addItem="
                    (obj) => {
                      prepareParamsArray('admin_emails', obj, index);
                    }
                  "
                  :block="fields.admin_emails"
                  :prefill-params="item"
                >
                  <template
                    slot="editButtons"
                    v-if="index !== params.settings.admin_emails.length && index !== 0"
                  >
                    <b-col class="align-self-end mb-4">
                      <b-button
                        @click="deleteItem(params.settings.admin_emails, index)"
                        variant="outline-primary"
                        size="sm"
                        style="height: 40px; width: 40px"
                      >
                        <i class="far fa-trash-alt fa-lg" />
                      </b-button>
                    </b-col>
                  </template>
                  <template slot="editButtons" v-else>
                    <b-col class="align-self-end mb-4">
                      <b-button
                        @click="addItem('admin_emails', { email: '', name: '' })"
                        variant="outline-primary"
                        size="sm"
                        style="height: 40px; width: 40px"
                      >
                        <i class="fas fa-plus fa-lg" />
                      </b-button>
                    </b-col>
                  </template>
                </ResourcesView>
              </div>
            </b-col>
            <!---->
            <!--Moderator Emails-->
            <b-col lg="6" v-if="params.settings.moderator_emails">
              <div>
                <h5 class="mb-3">
                  {{ fields.moderator_emails.title }}
                </h5>

                <ResourcesView
                  v-for="(item, index) in params.settings.moderator_emails"
                  :key="index"
                  @addItem="
                    (obj) => {
                      prepareParamsArray('moderator_emails', obj, index);
                    }
                  "
                  :block="fields.moderator_emails"
                  :prefill-params="item"
                >
                  <template
                    slot="editButtons"
                    v-if="index !== params.settings.moderator_emails.length && index !== 0"
                  >
                    <b-col class="align-self-end mb-4">
                      <b-button
                        @click="deleteItem(params.settings.moderator_emails, index)"
                        variant="outline-primary"
                        size="sm"
                        style="height: 40px; width: 40px"
                      >
                        <i class="far fa-trash-alt fa-lg" />
                      </b-button>
                    </b-col>
                  </template>
                  <template slot="editButtons" v-else>
                    <b-col class="align-self-end mb-4">
                      <b-button
                        @click="addItem('moderator_emails', { email: '', name: '' })"
                        variant="outline-primary"
                        size="sm"
                        style="height: 40px; width: 40px"
                      >
                        <i class="fas fa-plus fa-lg" />
                      </b-button>
                    </b-col>
                  </template>
                </ResourcesView>
              </div>
            </b-col>
            <!---->
          </b-row>
          <!---->
        </template>
      </ResourceWrapper>
      <!---->
      <!-- Transactional E-MAIL Settings BLOCK END -->

      <!-- Sales & CRM Platforms START -->
      <h3 v-if="params.settings.hubspot || params.settings.streak" class="text-uppercase">
        {{ $t('resources.block_titles.sales_crm_platforms') }}
        <custom-tooltip class="ml-2" :name="$t('resources.block_titles.sales_crm_platforms')">
          {{ $t('resources.tooltips.sales_and_crm_platforms') }}
        </custom-tooltip>
      </h3>

      <!--Hubspot-->
      <ResourceWrapper v-if="params.settings.hubspot" :block="fields.hubspot">
        <template slot="body">
          <ResourcesView
            @addItem="
              (obj) => {
                prepareParams(params.settings, obj, 'hubspot');
              }
            "
            :block="fields.hubspot"
            :prefill-params="params.settings.hubspot"
          />
        </template>
      </ResourceWrapper>
      <!---->

      <!--Streak-->
      <ResourceWrapper v-if="params.settings.streak" :block="fields.streak">
        <template slot="body">
          <ResourcesView
            @addItem="
              (obj) => {
                prepareParams(params.settings, obj, 'streak');
              }
            "
            :block="fields.streak"
            :prefill-params="params.settings.streak"
          />
        </template>
      </ResourceWrapper>
      <!---->
      <!-- Sales & CRM Platforms END -->

      <!-- E-MAIL & COMMS PLATFORMS START -->
      <h3>{{ $t('resources.block_titles.email_comms_platforms') }}</h3>

      <!--Mailchimp-->
      <ResourceWrapper v-if="params.settings.mailchimp" :block="fields.mailchimp">
        <template slot="body">
          <ResourcesView
            @addItem="
              (obj) => {
                prepareParams(params.settings, obj, 'mailchimp');
              }
            "
            :block="fields.mailchimp"
            :prefill-params="params.settings.mailchimp"
          />
        </template>
      </ResourceWrapper>
      <!---->

      <!--Klaviyo-->
      <ResourceWrapper v-if="params.settings.klaviyo" :block="fields.klaviyo">
        <template slot="body">
          <ResourcesView
            @addItem="
              (obj) => {
                prepareParams(params.settings, obj, 'klaviyo');
              }
            "
            :block="fields.klaviyo"
            :prefill-params="params.settings.klaviyo"
          />
        </template>
      </ResourceWrapper>
      <!---->

      <!--ActiveCampaign-->
      <ResourceWrapper v-if="params.settings.activecampaign" :block="fields.activecampaign">
        <template slot="body">
          <ResourcesView
            @addItem="
              (obj) => {
                prepareParams(params.settings, obj, 'activecampaign');
              }
            "
            :block="fields.activecampaign"
            :prefill-params="params.settings.activecampaign"
          />
        </template>
      </ResourceWrapper>
      <!---->

      <!--Campaign Monitor-->
      <ResourceWrapper :block="{ title: 'Campaign Monitor', icon: 'icon-campaignmonitor' }">
        <template slot="body">
          <ServiceConnect
            service="campaignmonitor"
            icon="icon-campaignmonitor"
            :resources_key="resourceKey"
            :connected="!!params.settings.campaignmonitor.oauth.access_token"
          />
        </template>
      </ResourceWrapper>
      <!---->

      <!--Slack-->
      <ResourceWrapper v-if="params.settings.slack" :block="fields.slack">
        <template slot="body">
          <ResourcesView
            @addItem="
              (obj) => {
                prepareParams(params.settings, obj, 'slack');
              }
            "
            :block="fields.slack"
            :prefill-params="params.settings.slack"
          />
        </template>
      </ResourceWrapper>
      <!---->
      <!-- E-MAIL & COMMS PLATFORMS END -->

      <!-- DATA MANAGEMENT PLATFORMS START -->
      <h3 v-if="params.settings.segment" class="text-uppercase">
        {{ $t('resources.block_titles.data_management_platforms') }}
      </h3>

      <!--Slack-->
      <ResourceWrapper v-if="params.settings.segment" :block="fields.segment">
        <template slot="body">
          <ResourcesView
            @addItem="
              (obj) => {
                prepareParams(params.settings, obj, 'segment');
              }
            "
            :block="fields.segment"
            :prefill-params="params.settings.segment"
          />
        </template>
      </ResourceWrapper>
      <!---->
      <!-- DATA MANAGEMENT PLATFORMS END -->

      <!-- Others START -->
      <h3 class="text-uppercase">
        {{ $t('resources.block_titles.others') }}
      </h3>

      <!--Firebase Settings-->
      <ResourceWrapper :block="fields.firebase">
        <template slot="body">
          <b-row
            class="mb-3"
            v-if="
              !isFirebaseUsed &&
                prefillParams.settings.firebase_account &&
                prefillParams.settings.firebase_account.active
            "
          >
            <b-col>
              {{ $t('resources.firebase.has_been_validated') }}
            </b-col>
          </b-row>
          <b-row v-if="!isFirebaseUsed">
            <b-col>
              <label :for="'web_app_config'">
                {{ $t('resources.firebase.web_app_config') }}
              </label>
              <b-form-textarea
                :rows="9"
                v-model="firebaseConfig"
                name="web_app_config"
                id="web_app_config"
              />
            </b-col>
            <b-col>
              <label :for="'service_account_config'">
                {{ $t('resources.firebase.service_account_config') }}
              </label>
              <b-form-textarea
                :rows="9"
                v-model="firebaseServiceAccount"
                name="service_account_config"
                id="service_account_config"
              />
            </b-col>
          </b-row>
          <b-row v-else>
            <b-col class="my-3">
              {{ $t('resources.firebase.has_been_set') }}
            </b-col>
          </b-row>
          <b-btn class="mt-3" v-if="!isFirebaseUsed" @click="firebaseValidation">
            {{ $t('resources.firebase.save_button') }}
          </b-btn>
        </template>
      </ResourceWrapper>
      <!---->

      <!--Settings-->
      <ResourceWrapper :block="fields.settings">
        <template slot="body">
          <ResourcesView
            @addItem="
              (obj) => {
                prepareParams(params.settings, obj);
              }
            "
            :block="fields.settings"
            :prefill-params="prefillParams.settings"
            checkbox-with="4"
          />
        </template>
      </ResourceWrapper>
      <!---->

      <!--Root Settings-->
      <ResourceWrapper v-if="isRoot" :block="fields.root_settings">
        <template slot="body">
          <ResourcesView
            @addItem="
              (obj) => {
                prepareParams(params.settings, obj);
              }
            "
            :block="fields.root_settings"
            :prefill-params="prefillParams.settings"
            checkbox-with="4"
          />
        </template>
      </ResourceWrapper>
      <!---->

      <!--ReCaptcha-->
      <ResourceWrapper v-if="params.settings.google_recaptcha" :block="fields.recaptcha">
        <template slot="body">
          <ResourcesView
            @addItem="
              (obj) => {
                prepareParams(params.settings.google_recaptcha, obj);
              }
            "
            :block="fields.recaptcha"
            :prefill-params="prefillParams.settings.google_recaptcha"
            checkbox-with="4"
          />
        </template>
      </ResourceWrapper>
      <!---->

      <!--Webhooks-->
      <ResourceWrapper v-if="params.settings.webhooks" :block="fields.webhook">
        <template slot="body">
          <ResourcesView
            v-for="(webhook, index) in params.settings.webhooks"
            :key="index"
            @addItem="
              (obj) => {
                prepareParamsArray('webhooks', obj, index);
              }
            "
            :prefill-params="webhook"
            :block="fields.webhook"
          >
            <template
              slot="editButtons"
              v-if="index !== params.settings.webhooks.length && index !== 0"
            >
              <b-col class="align-self-end mb-4">
                <b-button
                  @click="deleteItem(params.settings.webhooks, index)"
                  variant="outline-primary"
                  size="sm"
                  style="height: 40px; width: 40px"
                >
                  <i class="far fa-trash-alt fa-lg" />
                </b-button>
              </b-col>
            </template>
            <template slot="editButtons" v-else>
              <b-col class="align-self-end mb-4">
                <b-button
                  @click="addItem('webhooks', { url: '' })"
                  variant="outline-primary"
                  size="sm"
                  style="height: 40px; width: 40px"
                >
                  <i class="fas fa-plus fa-lg" />
                </b-button>
              </b-col>
            </template>
          </ResourcesView>
        </template>
      </ResourceWrapper>
      <!---->

      <!-- Others END -->

      <form-buttons class="pb-5" back-url="/" />
    </b-form>
  </div>
</template>

<script>
import fields from './resourcesEditingFields';
import ResourcesView from './ResourcesView';
import ResourceWrapper from './ResourceWrapper';
import ServiceConnect from '../../components/global/serviceConnect';
import StripeConnectBtn from '../../components/stripe/stripeConnectButton';
import { inviteMethods } from '../../mixins/invite';
import $ from 'jquery';
import axios from '@/utils/axios';
import ImageUploader from '@/components/global/imageUploader';
import { get } from 'lodash';
import ServiceAccountsEdit from './serviceAccounts/ServiceAccountsEdit.vue';
import yaml from 'js-yaml';
import { fillDefaultData } from '../../utils/object-utils';

export default {
  name: 'ResourceEditing',
  middleware: 'auth',
  components: {
    ResourcesView,
    ResourceWrapper,
    ServiceConnect,
    StripeConnectBtn,
    ImageUploader,
    ServiceAccountsEdit,
  },
  mixins: [inviteMethods],
  computed: {
    id() {
      return this.$route.query.resource || '';
    },
    resourceKey() {
      return this.$store.getters['resource/currentResource'].key;
    },
  },
  data() {
    return {
      firebaseConfig: null,
      firebaseServiceAccount: null,
      isFirebaseUsed: false,
      formValidated: false,
      fields: fields,
      prefillParams: {},
      loading: false,
      paymentSystemsLoading: false,
      itemPrepared: {},
      locked: [],
      params: {
        system_settings: {},
        settings: {},
      },
      data: {},
      debounceSubmit: () => {},
      isRoot: false,
      logo: {
        url: null,
      },
      initialUsers: [],
      loaders: {},
    };
  },
  beforeCreate() {
    if (this.$route.query.resource) {
      this.$store.dispatch('resource/updateResource', this.$route.query.resource);
    }
  },
  created() {
    if (this.$route.query.state && !this.$route.query.resource) {
      let params = { ...this.$route.query, ...{ resource: this.$route.query.state } };
      delete params.state;
      window.location.href = `${this.$route.path}?${$.param(params)}`;
    }

    this.isRoot = !!(this.$store.getters['auth/role'] === 'root');
  },
  async mounted() {
    if (!this.id) {
      this.$router.push({ path: '/resources' });
    } else {
      this.loading = true;

      await this.fetchData();
      await this.getFirebaseStatus();

      this.loading = false;

      this.handleStripeOAuth();
    }

    this.debounceSubmit = _.debounce(this.submit, 1000);
    this.getAvailablePlans();
  },
  methods: {
    firebaseValidation() {
      this.loading = true;
      this.putData(
        `resources/${this.$route.query.resource}/firebase/settings`,
        {
          firebase_account_web_app_config: yaml.load(this.firebaseConfig),
          firebase_account_service_account_config: yaml.load(this.firebaseServiceAccount),
        },
        false,
      )
        .then((resp) => {
          if (resp && resp.data) {
            this.firebaseConfig = null;
            this.firebaseServiceAccount = null;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchData() {
      return this.getData(`resources/${this.$route.query.resource}`).then((response) => {
        if (response) {
          const { data } = response;

          this.locked = data.locked;

          this.createPrefillData(data);
        } else {
          this.$router.push('/resources');
        }
      });
    },
    getFirebaseStatus() {
      return this.getData(
        `resources/${this.$route.query.resource}/firebase/settings/is-already-activated`,
      ).then(({ data }) => {
        this.firebaseConfig = null;
        this.firebaseServiceAccount = null;
        this.isFirebaseUsed = data.is_already_used;
      });
    },
    getDefaultUsersList() {
      return this.getData('users?limit=20').then(({data}) => {
        return data.items;
      });
    },
    getAvailablePlans() {
      this.getData('plans?limit=100').then(({ data }) => {
        this.$store.dispatch('user/updatePlans', data.items);
      });
    },
    async openIPAddresses(blockFieldId, fieldsData) {
      if (this.checkIfEditable(blockFieldId)) {
        this.toggleLoaderForBlock(blockFieldId, true);

        if (!this.initialUsers.length) {
          this.initialUsers = await this.getDefaultUsersList();
        }

        const usersField = this.fields[fieldsData].items.find((select) => select.name === 'user_id');
        if (usersField) {
          this.$set(usersField, 'options', this.initialUsers);
        }

        const mask_ips_users = this.params.settings[blockFieldId];
        const userIds = new Set();

        if (Array.isArray(mask_ips_users)) {
          mask_ips_users.forEach((mask) => {
            if (mask.mask_ip !== null || mask.user_id !== null) {
              userIds.add(mask.user_id);
            }
          });

          const userIdsArray = Array.from(userIds);
          if (userIdsArray.length) {
            this.getUsersByIDs(userIdsArray)
                .then((data) => {
                  this.params.settings[blockFieldId] = this.params.settings[blockFieldId].map(({ user_id, ...restData }) => {
                    const user = data.find((user) => user.id === user_id);
                    if (user) {
                      const { id: userId, first_name, last_name, email } = user;

                      return {
                        ...restData,
                        user_id: {
                          value: userId,
                          name: first_name && last_name ? `#${userId} ${first_name} ${last_name}` : `#${userId} ${email}`
                        }
                      }
                    } else {
                      return {
                        ...restData,
                        user_id
                      }
                    }
                  });
                }).finally(() => this.toggleLoaderForBlock(blockFieldId, false))
          } else {
            this.toggleLoaderForBlock(blockFieldId, false)
          }
        }
      }
    },
    toggleLoaderForBlock(blockName, value) {
      this.$set(this.loaders, blockName, value || false);
    },
    createPrefillData(data) {
      this.params.settings.default_paysystem = data.settings.default_paysystem;
      this.params.system_settings.default_paysystem = data.system_settings.default_paysystem;

      this.prefillParams = data;
      this.prefillParams.settings.rollback_not_confirmed_email = this.transformPrefillData(
        data.settings.rollback_not_confirmed_email,
      );
      this.prefillParams.settings.limit_device_authorization = this.transformDeviceAutorization(
        data.settings.limit_device_authorization,
      );
      this.prefillParams.settings.compromised_period = this.transformPrefillData(
        data.settings.compromised_period,
      );
      this.prefillParams.settings.default_paysystem = this.transformPrefillData(
        data.settings.default_paysystem,
      );

      if (
        !this.params.settings.default_paysystem &&
        this.params.system_settings.default_paysystem
      ) {
        this.fields.default_payment_system.items[0].disabled = true;
        this.params.settings.default_paysystem = this.params.system_settings.default_paysystem;
        this.prefillParams.settings.default_paysystem = this.transformPrefillData(
          this.params.system_settings.default_paysystem,
        );
      } else {
        this.fields.default_payment_system.items[0].disabled = false;
      }

      this.prefillParams.settings.delay_downgrade = this.transformPrefillData(
        data.settings.delay_downgrade,
      );
      this.prefillParams.invitation_landing_url =
        data.invitation_landing_url || this.defaultInvitationURL;

      this.params.settings.business = data.settings.business;
      this.params.settings.predictive_paywall = data.settings.predictive_paywall;

      this.logo.url = get(data, 'logo', null);

      if (this.checkIfEditable('compromised_limit') && data.settings.compromised_limit !== null) {
        this.prefillParams.settings.compromised_limit = data.settings.compromised_limit.toString();
      }

      if (this.checkIfEditable('default_guest_subscription_id')) {
        if (data.settings.default_guest_subscription_id) {
          this.getData(`subscriptions/${data.settings.default_guest_subscription_id}`).then(
            ({ data }) => {
              this.prefillParams.settings.default_guest_subscription_id = {
                label: data.title,
                value: data.id,
              };
            },
          );
        } else {
          this.prefillParams.settings.default_guest_subscription_id = null;
        }
      }

      if (this.checkIfEditable('default_subscription_id')) {
        if (data.settings.default_subscription_id) {
          this.getData(`subscriptions/${data.settings.default_subscription_id}`).then(
            ({ data }) => {
              this.prefillParams.settings.default_subscription_id = {
                label: data.title,
                value: data.id,
              };
            },
          );
        } else {
          this.prefillParams.settings.default_subscription_id = null;
        }
      }

      if (this.checkIfEditable('downgrade_subscription_id')) {
        if (data.settings.downgrade_subscription_id) {
          this.getData(`subscriptions/${data.settings.downgrade_subscription_id}`).then(
            ({ data }) => {
              this.prefillParams.settings.downgrade_subscription_id = {
                label: data.title,
                value: data.id,
              };
            },
          );
        } else {
          this.prefillParams.settings.downgrade_subscription_id = null;
        }
      }

      if (this.checkIfEditable('google_recaptcha')) {
        if (data.settings.google_recaptcha) {
          this.params.settings.google_recaptcha = {};
        }
      }

      if (this.checkIfEditable('streak')) {
        if (data.settings.streak) {
          this.params.settings.streak = {};
          this.params.settings.streak.api_key = data.settings.streak.api_key || '';
        } else {
          this.params.settings.streak = { api_key: '' };
        }
      }

      if (this.checkIfEditable('hubspot')) {
        if (data.settings.hubspot) {
          this.params.settings.hubspot = {};
          this.params.settings.hubspot.api_key = data.settings.hubspot.api_key || '';
        } else {
          this.params.settings.hubspot = { api_key: '' };
        }
      }

      if (this.checkIfEditable('campaignmonitor')) {
        if (data.settings.campaignmonitor) {
          this.params.settings.campaignmonitor = data.settings.campaignmonitor;
          this.params.settings.campaignmonitor.api_key =
            data.settings.campaignmonitor.api_key || '';
        } else {
          this.params.settings.campaignmonitor = { api_key: '' };
        }
      }

      if (this.checkIfEditable('emails_from')) {
        if (data.settings.emails_from) {
          this.params.settings.emails_from = {};
          this.params.settings.emails_from.name = data.settings.emails_from.name || '';
          this.params.settings.emails_from.email = data.settings.emails_from.email || '';
        } else {
          this.params.settings.emails_from = { email: '', name: '' };
        }
      }

      if (this.checkIfEditable('emails_replay_to')) {
        if (data.settings.emails_replay_to) {
          this.params.settings.emails_replay_to = {};
          this.params.settings.emails_replay_to.name = data.settings.emails_replay_to.name || '';
          this.params.settings.emails_replay_to.email = data.settings.emails_replay_to.email || '';
        } else {
          this.params.settings.emails_replay_to = { email: '', name: '' };
        }
      }

      if (this.checkIfEditable('mailchimp')) {
        if (data.settings.mailchimp) {
          this.params.settings.mailchimp = {};
          this.params.settings.mailchimp.id = data.settings.mailchimp.id || '';
          this.params.settings.mailchimp.key = data.settings.mailchimp.key || '';
          this.params.settings.mailchimp.name = data.settings.mailchimp.name || '';
        } else {
          this.params.settings.mailchimp = { id: '', key: '', name: '' };
        }
      }

      if (this.checkIfEditable('klaviyo')) {
        if (data.settings.klaviyo) {
          this.params.settings.klaviyo = {
            active: data.settings.klaviyo.active || false,
            public_key: data.settings.klaviyo.public_key || '',
            secret_key: data.settings.klaviyo.secret_key || '',
            paid_list_ids: data.settings.klaviyo.paid_list_ids || '',
            free_list_ids: data.settings.klaviyo.free_list_ids || '',
          };
        } else {
          this.params.settings.klaviyo = {
            active: false,
            public_key: '',
            secret_key: '',
            paid_list_ids: '',
            free_list_ids: '',
          };
        }
      }

      if (this.checkIfEditable('activecampaign')) {
        if (data.settings.activecampaign) {
          this.params.settings.activecampaign = {};
          this.params.settings.activecampaign.active = data.settings.activecampaign.active || false;
          this.params.settings.activecampaign.key = data.settings.activecampaign.key || '';
          this.params.settings.activecampaign.account = data.settings.activecampaign.account || '';
          this.params.settings.activecampaign.list_id = data.settings.activecampaign.list_id || '';
        } else {
          this.params.settings.activecampaign = { active: false, key: '', account: '', list_id: '' };
        }
      }

      if (this.checkIfEditable('segment')) {
        if (data.settings.segment) {
          this.params.settings.segment = {};
          this.params.settings.segment.write_key = data.settings.segment.write_key || '';
          this.params.settings.segment.active = data.settings.segment.active || false;
        } else {
          this.params.settings.segment = { write_key: '', active: false };
        }
      }

      if (this.checkIfEditable('slack')) {
        if (data.settings.slack) {
          this.params.settings.slack = {};
          this.params.settings.slack.webhook = data.settings.slack.webhook || '';
          this.params.settings.slack.channel = data.settings.slack.channel || '';
          this.params.settings.slack.username = data.settings.slack.username || '';
        } else {
          this.params.settings.slack = { webhook: '', channel: '', username: '' };
        }
      }

      if (this.checkIfEditable('stripe')) {
        if (data.settings.stripe) {
          this.params.settings.stripe = {};
          this.prefillParams.stripe_connect = {};

          const stripeSettings = data.settings.stripe;
          const stripeConnectSettings = data.settings.stripe_connect;

          const stripeModes = ['test', 'live'];

          stripeModes.forEach((mode) => {
            this.params.settings.stripe[mode] = {
              public_key: stripeSettings[mode].public_key || '',
              private_key: stripeSettings[mode].private_key || '',
            };

            this.prefillParams.stripe_connect[mode] = {
              stripe_connect_status: false,
              stripe_connect_url: stripeSettings[mode].stripe_connect_url || '',
            };

            if (stripeConnectSettings && stripeConnectSettings[mode]) {
              const isStripeConnectExists =
                !!stripeConnectSettings[mode].stripe_publishable_key &&
                !!stripeConnectSettings[mode].access_token;

              this.prefillParams.stripe_connect[mode] = {
                public_key: stripeConnectSettings[mode].stripe_publishable_key || '',
                private_key: stripeConnectSettings[mode].access_token || '',
              };

              this.prefillParams.stripe_connect[mode].stripe_connect_status = isStripeConnectExists;
            }
          });
        } else {
          const emptyKeys = {
            test: {
              public_key: '',
              private_key: '',
            },
            live: {
              public_key: '',
              private_key: '',
            },
          };

          this.params.settings.stripe = { ...emptyKeys };
        }
      }

      if (this.checkIfEditable('square')) {
        if (data.settings.square) {
          this.params.settings.square = {};

          const squareSettings = data.settings.square;

          const squareModes = ['test', 'live'];

          squareModes.forEach((mode) => {
            this.params.settings.square[mode] = {
              app_id: squareSettings[mode].app_id || '',
              location_id: squareSettings[mode].location_id || '',
              access_token: squareSettings[mode].access_token || '',
            };
          });
        } else {
          const emptyKeys = {
            test: {
              app_id: '',
              location_id: '',
              access_token: '',
            },
            live: {
              app_id: '',
              location_id: '',
              access_token: '',
            },
          };

          this.params.settings.stripe = { ...emptyKeys };
        }
      }

      if (this.checkIfEditable('stripe_connect_fee')) {
        this.params.settings.stripe_connect_fee = data.settings.stripe_connect_fee || '';
      }

      if (this.checkIfEditable('stripe_connect_fee_ti_events')) {
        this.params.settings.stripe_connect_fee_ti_event =
          data.settings.stripe_connect_fee_ti_event || '';
      }

      if (this.checkIfEditable('admin_emails')) {
        if (data.settings.admin_emails && data.settings.admin_emails.length > 0) {
          this.params.settings.admin_emails = [];
          data.settings.admin_emails.map((item) => {
            if (item.email !== '') {
              this.params.settings.admin_emails.push({
                email: item.email || '',
                name: item.name || '',
              });
            }
          });
          if (this.params.settings.admin_emails.length === 0) {
            this.params.settings.admin_emails = [{ email: '', name: '' }];
          }
        } else {
          this.params.settings.admin_emails = [{ email: '', name: '' }];
        }
      }

      if (this.checkIfEditable('moderator_emails')) {
        if (data.settings.moderator_emails && data.settings.moderator_emails.length > 0) {
          this.params.settings.moderator_emails = [];
          data.settings.moderator_emails.map((item) => {
            if (item.email !== '') {
              this.params.settings.moderator_emails.push({
                email: item.email || '',
                name: item.name || '',
              });
            }
          });
          if (this.params.settings.moderator_emails.length === 0) {
            this.params.settings.moderator_emails = [{ email: '', name: '' }];
          }
        } else {
          this.params.settings.moderator_emails = [{ email: '', name: '' }];
        }
      }

      if (this.checkIfEditable('webhooks')) {
        if (data.settings.webhooks && data.settings.webhooks.length > 0) {
          this.params.settings.webhooks = [];
          data.settings.webhooks.map((item) => {
            if (item.url !== '') {
              this.params.settings.webhooks.push({
                url: item.url || '',
              });
            }
          });
          if (this.params.settings.webhooks.length === 0) {
            this.params.settings.webhooks = [{ url: '' }];
          }
        } else {
          this.params.settings.webhooks = [{ url: '' }];
        }
      }

      if (this.checkIfEditable('blacklist')) {
        if (data.settings.blacklist && data.settings.blacklist.length > 0) {
          this.params.settings.blacklist = [];
          data.settings.blacklist.map((item) => {
            if (item.domain !== '') {
              this.params.settings.blacklist.push({
                domain: item.domain || '',
                msg: item.msg || '',
                notify: item.notify || false,
              });
            }
          });
          if (this.params.settings.blacklist.length === 0) {
            this.params.settings.blacklist = [{ domain: '', msg: '', notify: false }];
          }
        } else {
          this.params.settings.blacklist = [{ domain: '', msg: '', notify: false }];
        }
      }

      if (this.checkIfEditable('mask_ips_users') && !this.initialUsers.length) {
        const mask_ips_users = get(data, 'settings.mask_ips_users', [
          { mask_ip: null, user_id: null },
        ]);
        this.params.settings.mask_ips_users = [];

        if (Array.isArray(mask_ips_users)) {
          mask_ips_users.map((mask) => {
            if (mask.mask_ip !== null || mask.user_id !== null) {
              this.params.settings.mask_ips_users.push(mask);
            }
          });
        }
      }

      if (this.checkIfEditable('range_ips_users') && !this.initialUsers.length) {
        const mask_ips_users = get(data, 'settings.range_ips_users', [
          { ip_from: null, ip_to: null, user_id: null },
        ]);
        this.params.settings.range_ips_users = [];

        if (Array.isArray(mask_ips_users)) {
          mask_ips_users.map((mask) => {
            if (mask.user_id !== null) {
              this.params.settings.range_ips_users.push({
                user_id: mask.user_id,
                ip_from: mask.ip_from,
                ip_to: mask.ip_to
              });
            }
          });
        }
      }
    },
    transformDeviceAutorization(value) {
      if (value === 0) {
        return {
          value: value,
          name: 'Unlimited',
        };
      } else {
        return this.transformPrefillData(value);
      }
    },
    transformPrefillData(value, key = 'name') {
      if (value !== null && value !== undefined) {
        if (_.isObject(value)) {
          return value;
        } else if (key === 'label') {
          return {
            value: value,
            label: value,
          };
        } else {
          return {
            value: value,
            name: value,
          };
        }
      } else {
        return value;
      }
    },
    deleteItem(array, index) {
      this.deleteItemByIndex(array, index);
    },
    addItem(field, obj) {
      this.addReactiveItemToArray(this.params.settings[field], obj);
    },
    checkIfEditable(field) {
      return this.locked.indexOf(field) === -1;
    },
    prepareParamsArray(field, params, index) {
      this.params.settings[field][index][params.name] = params.value;
    },
    prepareParams(paramsObj, params, field) {
      if (field) {
        paramsObj[field] = this.createObject(paramsObj[field]);
        paramsObj[field][params.name] = this.formatValue(params.value);
      } else {
        paramsObj[params.name] = this.formatValue(params.value);
      }

      this.$forceUpdate();
    },
    async handleStripeOAuth() {
      try {
        const { stripeCode: stripeConOAuthCode, mode: stripeConOAuthMode } = this.$route.query;

        if (stripeConOAuthCode && stripeConOAuthMode) {
          await this.completeStripeConnectOAuth(stripeConOAuthCode, stripeConOAuthMode);
        }

        if (this.params.settings.stripe) {
          return this.getStripeConnectOAuthURLs();
        }
      } catch (e) {
        console.log('ERROR:', e);
      }
    },
    getStripeConnectOAuthURLs() {
      this.paymentSystemsLoading = true;

      return axios
        .get('admin/integrations/stripe/oauth')
        .then((response) => {
          if (response) {
            const {
              data: { live, test },
            } = response;

            if (this.params.settings.stripe) {
              if (live) {
                this.prefillParams.stripe_connect.live.stripe_connect_url = live.authorize_url;
                this.prefillParams.stripe_connect.live.stripe_connect_status =
                  live.type !== 'authorize';
              }

              if (test) {
                this.prefillParams.stripe_connect.test.stripe_connect_url = test.authorize_url;
                this.prefillParams.stripe_connect.test.stripe_connect_status =
                  test.type !== 'authorize';
              }
            }
          }
        })
        .catch((error) => console.log(error))
        .finally(() => (this.paymentSystemsLoading = false));
    },
    completeStripeConnectOAuth(code, mode) {
      if (!code)
        throw new Error('Stripe Connect OAuth confirmation code is not provided as argument');
      if (!mode)
        throw new Error('Stripe Connect OAuth confirmation mode is not provided as argument');

      this.clearExcessURLQuery();

      const paymentSystemsSettingsBlock = this.$refs.paymentSystemsSettings;

      if (paymentSystemsSettingsBlock) {
        setTimeout(() => paymentSystemsSettingsBlock.scrollIntoView());
      }

      this.paymentSystemsLoading = true;

      return axios
        .post('admin/integrations/stripe/oauth', { code, mode })
        .then((response) => {
          if (response) {
            const {
              data: { stripe_publishable_key, access_token },
            } = response;

            if (!!stripe_publishable_key && !!access_token) {
              this.prefillParams.stripe_connect[mode].public_key = stripe_publishable_key || '';
              this.prefillParams.stripe_connect[mode].private_key = access_token || '';

              this.prefillParams.stripe_connect[mode].stripe_connect_status = true;
            }
          }
        })
        .catch((error) => console.log(error))
        .finally(() => (this.paymentSystemsLoading = false));
    },
    disconnectStripe(mode) {
      this.paymentSystemsLoading = true;

      return axios
        .post('admin/integrations/stripe/deauthorize', { mode })
        .then((response) => {
          if (response) {
            if (response.data && response.data.status === 'ok') {
              this.prefillParams.stripe_connect[mode].stripe_connect_status = false;
            }
          }
        })
        .then(this.getStripeConnectOAuthURLs)
        .catch((error) => console.log(error))
        .finally(() => (this.paymentSystemsLoading = false));
    },
    validateMaskIp() {
      if (Array.isArray(this.params.settings.mask_ips_users)) {
        this.params.settings.mask_ips_users.forEach((item) => {
          if (
            (!item.user_id && (!item.mask_ip || item.mask_ip === '_._._._/_')) ||
            (item.user_id !== null && this.maskIPValidator(item.mask_ip))
          ) {
            return;
          }
          this.$noty.error('invalid IP Mask Settings data');
          throw new Error('validateMaskIp Error');
        });
      }
    },
    formatUsersId() {
      if (Array.isArray(this.params.settings.mask_ips_users)) {
        this.params.settings.mask_ips_users = this.params.settings.mask_ips_users.map((item) => {
          if (item.user_id) {
            return {
              mask_ip: item.mask_ip === '_._._._/_' || item.mask_ip === '' ? null : item.mask_ip,
              user_id: item.user_id.value,
            };
          }
        });
        this.params.settings.mask_ips_users = this.params.settings.mask_ips_users.filter(
          (item) => !!item,
        );
      }
      this.maskIPsUsersDefaultFill();
    },
    maskIPsUsersDefaultFill() {
      if (!!this.params.settings.mask_ips_users) {
        if (Array.isArray(this.params.settings.mask_ips_users)) {
          if (
            !(
              this.params.settings.mask_ips_users.length === 1 &&
              this.params.settings.mask_ips_users[0].mask_ip === null &&
              this.params.settings.mask_ips_users[0].user_id === null
            )
          ) {
            if (
              this.params.settings.mask_ips_users.length === 0 ||
              (this.params.settings.mask_ips_users.length === 1 &&
                this.params.settings.mask_ips_users[0].user === null &&
                (this.params.settings.mask_ips_users[0].mask_ip === '' ||
                  this.params.settings.mask_ips_users[0].mask_ip === '_._._._/_'))
            ) {
              this.params.settings.mask_ips_users = [{ mask_ip: null, user_id: null }];
            }
          }
          return;
        }
      }
      this.params.settings.mask_ips_users = [{ mask_ip: null, user_id: null }];
    },
    formatFinalData(data) {
      const localData = structuredClone(data);

      localData.settings.mask_ips_users = fillDefaultData(localData.settings.mask_ips_users.map((item) => {
        if (item.user_id) {
          return {
            mask_ip: item.mask_ip === '_._._._/_' || item.mask_ip === '' ? null : item.mask_ip,
            user_id: item.user_id.value || item.user_id,
          };
        }
      }), [{ mask_ip: null, user_id: null }])

      localData.settings.range_ips_users = fillDefaultData(localData.settings.range_ips_users.map((item) => {
        if (item.user_id) {
          return {
            ip_from: item.ip_from === '_._._._/_' || item.ip_from === '' ? null : item.ip_from,
            ip_to: item.ip_to === '_._._._/_' || item.ip_to === '' ? null : item.ip_to,
            user_id: item.user_id.value || item.user_id,
          };
        }
      }), [{ ip_from: null, ip_to: null, user_id: null }]);

      return localData;
    },
    submit() {
      this.validateMaskIp();
      this.formValidated = true;

      if (this.$refs.form.checkValidity()) {
        if (this.locked.length > 0) {
          this.locked.map((key) => {
            if (this.params.settings.hasOwnProperty(key)) {
              delete this.params.settings[key];
            }
          });
        }

        this.putData(`resources/${this.id}`, this.formatFinalData(this.params)).then(({ data }) => {
          if (data) {
            this.locked = data.locked;
            this.createPrefillData(data);

            this.$store.dispatch('resource/updateResourceData', {
              host: data.host,
              key: data.public_key,
              title: data.title,
              invitation_landing_url: data.invitation_landing_url,
            });

            this.$store.dispatch('resource/fetchResources');
          }
        });
        if (Array.isArray(this.params.settings.mask_ips_users)) {
          if (
            this.params.settings.mask_ips_users.length === 1 &&
            this.params.settings.mask_ips_users[0].mask_ip === null &&
            this.params.settings.mask_ips_users[0].user_id === null
          ) {
            this.params.settings.mask_ips_users = [];
          }
        }
      }
    },
    clearExcessURLQuery() {
      this.$router.replace({ query: { resource: this.resourceKey } });
    },
    async getUsersByIDs (userIds) {
      return await this.getData(`users`, { params: { filter: { 'users.id': userIds }} }).then(({data}) => {
        if (data) {
          return data.items;
        }
      })
    },
  },
  watch: {
    resourceKey() {
      this.$router.push({ path: `/resources/edit?resource=${this.resourceKey}` });
      this.params = { settings: {}, system_settings: {} };
      this.prefillParams = {};
      this.loading = true;
      this.getData(`resources/${this.resourceKey}`)
        .then(({ data }) => {
          this.locked = data.locked;
          this.createPrefillData(data);
          this.getAvailablePlans();
          this.getFirebaseStatus();
        })
        .then(() => {
          this.loading = false;
          this.$forceUpdate();
        });
    },
  },
};
</script>
